import { getNumberOfPages, getPDFPageBoxes, isCorrupted, isEncrypted } from '@design-stack-ct/assets-sdk';
import {
  DesignSpecServiceResponse,
  getSpecificationItemsAsync,
  Orientation,
  ProductOptions,
  SurfaceSpecBoundsRectangle,
  ValidationState,
} from '@vp/upload-and-print-validator';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { currentCulture } from 'src/utils/cultureUtil';
import { addPageAction } from './newRelicUtil';

const CM_TO_INCHES = 0.3937007874;
type CompletedProductInformation = {
  productKey: string;
  productVersion: number;
  selectedOptions: ProductOptions;
};
function getPositionForType(
  surfaceType: string,
  specifications: DesignSpecServiceResponse
): SurfaceSpecBoundsRectangle[] {
  const results = [];
  for (let i = 0; i < specifications.designViews.length; i++) {
    const surface = specifications.designViews[i];
    const guide = surface.docAdditionalData.masks.find(guide => guide.pathType === surfaceType);

    if (guide) {
      results[i] = guide.boundingArea.position;
    }
  }

  return results;
}

type ValidationAttributePairType = Record<string, string>;
function isPageOrientationValidationApplicable(productOptions: Record<string, string>): boolean {
  return (
    Object.entries(getValidationAttributePair(['Orientation', 'Is Vertical', 'Product Orientation'], productOptions))
      .length > 0
  );
}
function getValidationAttributePair(
  aliases: string[],
  validationData: ValidationAttributePairType
): ValidationAttributePairType {
  return aliases
    .filter(alias => !!validationData[alias])
    .reduce((acc, curr) => {
      if (curr) {
        acc['key'] = curr;
        acc['value'] = validationData[curr];
      }
      return acc;
    }, {} as ValidationAttributePairType);
}

export const clientSideValidation = async (
  file: File,
  completedProductInformation: CompletedProductInformation | null,
  abortSignal: AbortSignal,
  fileUri: string
) => {
  if (completedProductInformation) {
    // Intentionally added Promise.all to allow for future parallel async calls for studio configuration need for ImageResolution.
    Promise.all([
      getSpecificationItemsAsync(
        {
          productKey: completedProductInformation.productKey,
          productVersion: completedProductInformation.productVersion,
          optionSelections: completedProductInformation.selectedOptions,
          culture: currentCulture.toLowerCase(),
          tenant: 'vista-print',
        },
        { ...getAuthHeader(), signal: abortSignal }
      ),
    ]).then(async ([designSpecification]) => {
      const start = new Date().getTime();
      await Promise.all([
        getCorruptValidation(file, fileUri),
        getEncryptedValidation(file, fileUri),
        getPageCountValidation(file, designSpecification, fileUri),
        getShapeValidation(file, completedProductInformation.selectedOptions, fileUri),
        getPageOrientation(file, designSpecification, fileUri),
        getPageSizeValidation(file, designSpecification, fileUri, completedProductInformation.selectedOptions),
      ]);

      const end = new Date().getTime();
      addPageAction('All Client Side validation', {
        fileUri,
        durationMs: end - start,
      });
    });
  }
};

const getPageSizeValidation = async (
  file: File,
  designSpecification: DesignSpecServiceResponse,
  fileUri: string,
  productOptions: ProductOptions
) => {
  const start = performance.now();
  const MinSizeTrimMultiple = 1;
  const MaxSizeTrimMultiple = 3;
  const pimPageCount = designSpecification.designViews.length;
  const bleedPositions = getPositionForType('BLEED', designSpecification);
  const trimPositions = getPositionForType('TRIM', designSpecification);
  const customerPageCount = await getNumberOfPages(file);
  const numberOfPagesToCheck = Math.min(Math.min(bleedPositions.length, pimPageCount), customerPageCount);
  const pageErrors = [];
  const pageWarnings = [];
  const pdfInfo = [];
  for (let i = 0; i < numberOfPagesToCheck; i++) {
    let pimPageWidth = Math.round((bleedPositions[i].width as number) * CM_TO_INCHES * 100) / 100;
    let pimPageHeight = Math.round((bleedPositions[i].height as number) * CM_TO_INCHES * 100) / 100;

    const trimPosition = trimPositions[i];
    let pimTrimX = pimPageWidth - Math.round((trimPosition.width as number) * CM_TO_INCHES * 100) / 100;
    let pimTrimY = pimPageHeight - Math.round((trimPosition.height as number) * CM_TO_INCHES * 100) / 100;
    // Get the specification orientation
    const specificationOrientation = pimPageWidth > pimPageHeight ? Orientation.Horizontal : Orientation.Vertical;
    // Get the customer page boxes
    const uploadedPDFBoxes = await getPDFPageBoxes(file, i + 1);
    const uploadedPDFPageWidth = uploadedPDFBoxes.SizeInfo?.WidthIn as number;
    const uploadedPDFPageHeight = uploadedPDFBoxes.SizeInfo?.HeightIn as number;
    pdfInfo.push(uploadedPDFBoxes);
    const isOrientationChangeAllowed = isPageOrientationValidationApplicable(productOptions);
    // update if page orientation change allowed
    if (isOrientationChangeAllowed) {
      const customerDocumentOrientation =
        uploadedPDFPageWidth > uploadedPDFPageHeight ? Orientation.Horizontal : Orientation.Vertical;
      // check each page's orientation
      if (customerDocumentOrientation !== specificationOrientation) {
        [pimPageWidth, pimPageHeight] = [pimPageHeight, pimPageWidth];
        [pimTrimX, pimTrimY] = [pimTrimY, pimTrimX];
      }
    }
    // setup the overall min/max sizes for the selected product'
    const maxAllowedWidthSize = pimPageWidth + pimTrimX * MaxSizeTrimMultiple;
    const maxAllowedHeightSize = pimPageHeight + pimTrimY * MaxSizeTrimMultiple;
    const minAllowedWidthSize = pimPageWidth - pimTrimX * MinSizeTrimMultiple;
    const minAllowedHeightSize = pimPageHeight - pimTrimY * MinSizeTrimMultiple;
    if (uploadedPDFPageHeight === pimPageHeight && uploadedPDFPageWidth === pimPageWidth) {
      continue; // Page size is correct
    }
    if (
      uploadedPDFPageWidth + 0.05 * uploadedPDFPageWidth < minAllowedWidthSize ||
      uploadedPDFPageWidth - 0.05 * uploadedPDFPageWidth > maxAllowedWidthSize ||
      uploadedPDFPageHeight + 0.05 * uploadedPDFPageHeight < minAllowedHeightSize ||
      uploadedPDFPageHeight - 0.05 * uploadedPDFPageHeight > maxAllowedHeightSize
    ) {
      pageErrors.push({ pageNumber: i + 1, expectedWidth: pimPageWidth, expectedHeight: pimPageHeight });
    } else if (uploadedPDFPageWidth < pimPageWidth || uploadedPDFPageHeight < pimPageHeight) {
      pageWarnings.push({ pageNumber: i + 1, expectedWidth: pimPageWidth, expectedHeight: pimPageHeight });
    }
  }

  const end = performance.now();
  addPageAction('File PageSize Check', {
    fileUri,
    durationMs: end - start,
    state:
      pageErrors.length > 0
        ? ValidationState.Error
        : pageWarnings.length > 0
        ? ValidationState.Warning
        : ValidationState.Ok,
    clientHelperFunctionOutput: pdfInfo,
  });
};

const getPageOrientation = async (file: File, designSpecification: DesignSpecServiceResponse, fileUri: string) => {
  const start = performance.now();
  const pimPageCount = designSpecification.designViews.length;
  const bleedPositions = getPositionForType('BLEED', designSpecification);
  const customerPageCount = await getNumberOfPages(file);
  const numberOfPagesToCheck = Math.min(Math.min(bleedPositions.length, pimPageCount), customerPageCount);
  const pdfInfo = [];
  // check that each page has the same orientation as the specifications service
  for (let i = 0; i < numberOfPagesToCheck; i++) {
    const pimPageWidth = Math.round((bleedPositions[i].width as number) * 100) / 100;
    const pimPageHeight = Math.round((bleedPositions[i].height as number) * 100) / 100;

    const specificationAspectRatio = pimPageWidth / pimPageHeight;

    if (specificationAspectRatio === 1) {
      continue; //Don't try to validate orientation if the product is a square
    }
    // Get the specification orientation
    const specificationOrientation = pimPageWidth > pimPageHeight ? Orientation.Horizontal : Orientation.Vertical;
    // Get the customer page boxes
    const uploadedPDFBoxes = await getPDFPageBoxes(file, i + 1);
    const uploadedPDFPageWidth = uploadedPDFBoxes.SizeInfo?.WidthIn as number;
    const uploadedPDFPageHeight = uploadedPDFBoxes.SizeInfo?.HeightIn as number;
    pdfInfo.push(uploadedPDFBoxes);
    // Get the page orientation
    const customerDocumentOrientation =
      uploadedPDFPageWidth > uploadedPDFPageHeight ? Orientation.Horizontal : Orientation.Vertical;
    // check each page's orientation
    if (customerDocumentOrientation !== specificationOrientation) {
      const end = performance.now();
      addPageAction('File Orientation Check', {
        fileUri,
        durationMs: end - start,
        state: ValidationState.Error,
        clientHelperFunctionOutput: pdfInfo,
      });
      return;
    }
  }

  const end = performance.now();
  addPageAction('File Orientation Check', {
    fileUri,
    durationMs: end - start,
    state: ValidationState.Ok,
    clientHelperFunctionOutput: pdfInfo,
  });
};

const getShapeValidation = async (file: File, options: ProductOptions, fileUri: string) => {
  const start = performance.now();
  const SquareProportionedOptionSelections = ['Circle', 'Rounded Square'];
  const FourByFiveAspectRatio = 4 / 5;
  const FiveByFourAspectRatio = 5 / 4;
  const pdfFirstPageBoxes = await getPDFPageBoxes(file, 1);
  const uploadedPDFPageWidth = pdfFirstPageBoxes.SizeInfo.WidthIn ?? 1;
  const uploadedPDFPageHeight = pdfFirstPageBoxes.SizeInfo.HeightIn ?? 1;
  const customerAspectRatio = uploadedPDFPageWidth / uploadedPDFPageHeight;
  const shape = options['Shape'];
  const shouldBeSquare = SquareProportionedOptionSelections.some(val => val === shape);
  const validation = {
    state: ValidationState.Ok,
  };
  if (shouldBeSquare) {
    if (customerAspectRatio === 1) {
      validation.state = ValidationState.Ok;
    } else if (customerAspectRatio < FiveByFourAspectRatio && customerAspectRatio > FourByFiveAspectRatio) {
      validation.state = ValidationState.Warning;
    } else {
      validation.state = ValidationState.Error;
    }
  } else {
    if (customerAspectRatio < FiveByFourAspectRatio && customerAspectRatio > FourByFiveAspectRatio) {
      //If it's between 4:5 and 5:4, that's close enough to square to cause an error
      validation.state = ValidationState.Error;
    } else {
      validation.state = ValidationState.Ok;
    }
  }
  const end = performance.now();
  addPageAction('File Shape Check', {
    fileUri,
    durationMs: end - start,
    state: validation.state,
    clientHelperFunctionOutput: validation.state !== ValidationState.Ok && { shouldBeSquare, customerAspectRatio },
  });
};

const getPageCountValidation = async (file: File, designSpecification: DesignSpecServiceResponse, fileUri: string) => {
  const start = performance.now();
  const customerPageCount = await getNumberOfPages(file);
  const pimPageCount = designSpecification.designViews.length;
  const isPageCountCorrect = customerPageCount === pimPageCount;
  const end = performance.now();
  addPageAction('File PageCount Check', {
    fileUri,
    durationMs: end - start,
    state: isPageCountCorrect ? ValidationState.Ok : ValidationState.Error,
    clientHelperFunctionOutput: { pageCount: customerPageCount, pimPageCount },
  });
};

const getCorruptValidation = async (file: File, fileUri: string) => {
  const start = performance.now();
  const isNotFileCorrupted = await isCorrupted(file);
  const end = performance.now();
  addPageAction('File Corruption Check', {
    fileUri,
    durationMs: end - start,
    state: isNotFileCorrupted ? ValidationState.Ok : ValidationState.Error,
    clientHelperFunctionOutput: isNotFileCorrupted,
  });
};

const getEncryptedValidation = async (file: File, fileUri: string) => {
  const start = performance.now();
  const isNotEncryptedValue = await isEncrypted(file);
  const end = performance.now();
  addPageAction('File Encryption Check', {
    fileUri,
    durationMs: end - start,
    state: isNotEncryptedValue ? ValidationState.Ok : ValidationState.Error,
    clientHelperFunctionOutput: isNotEncryptedValue,
  });
};
